import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';

import { UtilityService } from '@casinocore/platform/core';
import { elementScroll } from '@casinocore/platform/utils';
import { CasinoTheme } from '@frontend/casino-ui/shared';
import { DeviceService } from '@frontend/vanilla/core';

import { CategoryNavigationComponent } from './category-navigation/category-navigation.component';
import { FixedNavigationComponent } from './fixed-navigation/fixed-navigation.component';
import { MobileNavigationComponent } from './mobile-navigation/mobile-navigation.component';
import { ICategory, PillSubNavConfig } from './pills-sub-navigation.model';

export interface IExtendedTopMenuItem {
    title: string;
    url: string;
    cssClass: string;
    name: string;
    condition?: string;
    iconBackgroundColor: string;
    iconTextColor: string;
    pathname?: string;
    dslResult: boolean;
}

@Component({
    selector: 'cc-pill-sub-navigation',
    templateUrl: 'pill-sub-navigation.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FixedNavigationComponent, MobileNavigationComponent, CategoryNavigationComponent],
})
export class PillSubNavigationComponent implements OnInit, AfterViewInit {
    myNavCategories = input<ICategory[]>([]);
    navItems = input<ICategory[]>([]);
    config = input<PillSubNavConfig>({} as PillSubNavConfig);
    defaultPageText = input<{ url: string; text: string }>({ url: '', text: '' });
    extendTopMenuState = input<boolean>(false);
    categorySelected = input<string>('');
    defaultCategoryType = input<ICategory>({} as ICategory);
    extendTopMenu = input<IExtendedTopMenuItem[]>([]);

    readonly showSearch = output<boolean>();
    readonly goToDefaultPage = output<string>();
    readonly goToPromotionPage = output<boolean>();
    readonly goToUrl = output<string>();
    readonly setActiveMobile = output<string>();
    readonly categoryTracking = output<{ event: Event; index: number; categoryid: string }>();
    readonly extentRightMenuTracking = output<{ url: string; categoryid: string }>();
    readonly thumbnailScroll = output<boolean>();

    @ViewChild('epcotNavTabContent', { read: ElementRef }) public epcotNavTabContent: ElementRef<HTMLDivElement>;
    @ViewChild('epcotNavContainer', { static: false }) epcotNavContainer: ElementRef<HTMLDivElement>;
    @ViewChildren('pillRef') pillRefs!: QueryList<ElementRef>;

    private deviceService = inject(DeviceService);
    private readonly utilityService = inject(UtilityService);

    private DEFAULT_OFFSET: number = 200;
    private previousCategoryType: string;
    private showEpcotNavLeftScrollButton = signal<boolean>(false);
    private showEpcotNavRightScrollButton = signal<boolean>(false);
    readonly categoryType = computed(() => this.config().categoryType);

    readonly processedMyNavCategories = computed(() => {
        const categories = this.myNavCategories() ?? [];
        return categories.map((category) => ({
            ...category,
            classMap: this.getCategoryClass(category),
        }));
    });

    readonly showMobileNav = computed(() => {
        return this.defaultCategoryType() || !this.config().isTouch || this.config().ForceMobileNav;
    });

    readonly showFixedNavPill = computed(() => {
        const cfg = this.config();
        return (
            cfg.showSearchInTopNavigation ||
            cfg.showHomeFixedNavigation ||
            (cfg.fixednavEnabled && cfg.showHomeInTopNavigation) ||
            cfg.isPromotionPagesEnabled
        );
    });

    public currentTheme: CasinoTheme;

    public scrollButtons = computed(() => [
        {
            id: 'left-scroll-navbar',
            direction: 'left',
            clickHandler: ($event: Event) => this.onMoveEpcotNavContainer(true, $event),
            condition: this.showEpcotNavLeftScrollButton(),
        },
        {
            id: 'right-scroll-navbar',
            direction: 'right',
            clickHandler: ($event: Event) => this.onMoveEpcotNavContainer(false, $event),
            condition: this.showEpcotNavRightScrollButton(),
        },
    ]);

    ngOnInit(): void {
        this.currentTheme = this.utilityService.getCurrentTheme();
    }
    constructor() {
        effect(() => {
            const currentCategoryType = this.categoryType();
            if (this.previousCategoryType !== currentCategoryType) {
                requestAnimationFrame(() => {
                    this.elementInViewPortCheck();
                    this.adjustEpcotNavContainerScroll();
                });
                this.previousCategoryType = currentCategoryType;
            }
        });
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            setTimeout(() => {
                this.elementInViewPortCheck();
                this.adjustEpcotNavContainerScroll();
            }, 0);
        });
    }

    handleShowSearch(): void {
        this.showSearch.emit(true);
    }

    handleGoToDefaultPage(source: string): void {
        this.goToDefaultPage.emit(source);
    }

    handleGoToPromotionPage(): void {
        this.goToPromotionPage.emit(true);
    }

    handleGoToUrl(url: string): void {
        this.goToUrl.emit(url);
    }

    handleSetActiveMobile(categoryId: string): void {
        this.setActiveMobile.emit(categoryId);
    }

    handleCategoryTracking(event: Event, index: number, categoryid: string): void {
        this.categoryTracking.emit({ event, index, categoryid });
    }

    handleExtentRightMenuTracking(url: string, categoryid: string): void {
        this.extentRightMenuTracking.emit({ url, categoryid });
    }

    handleChildExtentRightMenuTracking(data: { url: string; categoryid: string }): void {
        this.extentRightMenuTracking.emit(data);
    }

    handleChildCategoryTracking(data: { event: Event; index: number; categoryid: string }): void {
        this.categoryTracking.emit(data);
    }

    handleThumbnailScroll(): void {
        this.thumbnailScroll.emit(true);
    }

    onMoveEpcotNavContainer(isLeft: boolean, event: Event): void {
        const { showLeft, showRight } = elementScroll(this.epcotNavContainer?.nativeElement, this.DEFAULT_OFFSET, isLeft, 0, event);
        this.showEpcotNavLeftScrollButton.set(showLeft);
        this.showEpcotNavRightScrollButton.set(showRight);
    }

    adjustEpcotNavContainerScroll(): void {
        let offsetLeft = 0;
        const navContainer = this.epcotNavContainer?.nativeElement;
        const activePill = navContainer?.querySelector('.nav-pills .active') as HTMLElement;
        if (activePill) {
            offsetLeft = activePill.clientWidth + activePill.offsetLeft;
            if (offsetLeft >= navContainer.clientWidth) {
                offsetLeft = offsetLeft - navContainer.clientWidth;
                offsetLeft = offsetLeft <= this.DEFAULT_OFFSET ? this.DEFAULT_OFFSET : offsetLeft;
            } else offsetLeft = 0;
        } else if (navContainer && navContainer.scrollWidth > navContainer.clientWidth) {
            offsetLeft = 0;
        } else {
            return;
        }
        const { showLeft, showRight } = elementScroll(navContainer, offsetLeft, false, 0);
        this.showEpcotNavLeftScrollButton.set(showLeft);
        this.showEpcotNavRightScrollButton.set(showRight);
    }

    elementInViewPortCheck(): void {
        if (!this.config()?.categoryType) return;

        const matchingPill = this.pillRefs.find((pill) => pill.nativeElement.id === this.config().categoryType);

        if (matchingPill) {
            const element = matchingPill.nativeElement;
            const dimensions = element.getBoundingClientRect();
            const viewportWidth = this.deviceService.windowWidth();
            const viewportHeight = this.deviceService.windowRect()?.height;

            const isInViewport =
                dimensions.top >= 0 && dimensions.left >= 0 && dimensions.right <= viewportWidth && dimensions.bottom <= viewportHeight;

            const isOverflowingRight = dimensions.right > viewportWidth;

            if (!isInViewport && isOverflowingRight) {
                const rightMove = dimensions.right - viewportWidth;
                if (this.config().isTouch) {
                    this.epcotNavTabContent?.nativeElement.scrollTo({
                        left: this.epcotNavTabContent.nativeElement.scrollLeft + rightMove,
                        behavior: 'smooth',
                    });
                }
            }
        }
    }

    protected isBadgeTypeSignPosting(signPostingCount: number) {
        return this.config().isSignPosting && signPostingCount > 0;
    }

    onPillClick(category: ICategory, $event: Event) {
        if (category.isNavigationCategory) {
            this.handleSetActiveMobile(category.categoryid);
            this.handleCategoryTracking($event, category.index, category.categoryid);
        } else if (category.isExtendedTopMenuItem) {
            this.handleExtentRightMenuTracking(category.url, category.categoryid);
        }
    }

    isSelected(category: ICategory) {
        return this.config().categoryType === category.categoryid || (this.config().currentUrl === category.url && category.fromExtendedMenuRight);
    }

    getCategoryClass(category: ICategory) {
        return {
            'active': this.isSelected(category),
            'casino-active-thumnail': this.isActiveThumbnail(category?.displayThumbnail),
            'from-extended-menu-right': category.fromExtendedMenuRight,
            'active-sign-posting': this.isBadgeTypeSignPosting(category.signPostingCount),
            [category.categoryid]: !!category.categoryid,
        };
    }

    isActiveThumbnail(displayThumbnail: boolean) {
        return displayThumbnail && this.config().displayThumbnailMobile && this.config().isDefaultCategory;
    }
}
